import React from 'react';
import ManualBookShell from './manual/ManualBookShell';

const circuitoAdmin = [
  {
    title: '1. Llega el pedido',
    body: 'El paciente solicitó un medicamento (bot, call center u otro canal). El caso queda en cola hasta que administración lo revise.'
  },
  {
    title: '2. Derivás al médico',
    body: 'En Gestión Derivación revisás paciente y medicamento, elegís el profesional y, si hace falta, dejás un comentario. El caso pasa a Pedidos/Recetas del médico.'
  },
  {
    title: '3. El médico decide',
    body: 'El profesional aprueba o rechaza en su pantalla. Vos no reemplazás esa decisión clínica.'
  },
  {
    title: '4. WhatsApp de alta (si está activo)',
    body: 'Si el médico activó el aviso al aprobar, el paciente recibe el mensaje de plan aprobado en ese momento.'
  },
  {
    title: '5. Registrás cada emisión',
    body: 'En Recetas a emitir cargás número y enlace cuando la receta ya fue emitida en el sistema externo. Ahí también se dispara el WhatsApp de emisión, si estaba activo en la aprobación.'
  }
];

const accesosOperativos = [
  {
    pantalla: 'Gestión Derivación',
    descripcion: 'Derivar pedidos, historial, canal interno con el médico y seguimiento por profesional.'
  },
  {
    pantalla: 'Recetas a emitir',
    descripcion: 'Registrar emisiones con número y enlace; ver pendientes por fecha prevista.'
  },
  {
    pantalla: 'Médicos derivación',
    descripcion: 'Definir qué médicos aparecen al derivar (solo si te habilitaron la pantalla).'
  },
  {
    pantalla: 'Administrativos emisión',
    descripcion: 'Definir qué personas administrativas pueden emitir cuando el médico asigna emisión al equipo (solo referentes).'
  },
  {
    pantalla: 'Trazabilidad recetas',
    descripcion: 'Auditoría del ciclo del pedido (solo si te asignaron el permiso).'
  },
  {
    pantalla: 'Este manual',
    descripcion: 'Guía operativa del rol administrativo.'
  }
];

const manualPages = [
  { id: 'portada', label: 'Portada', eyebrow: 'Manual', title: 'Guía del área administrativa' },
  { id: 'alcance', label: 'Intro', eyebrow: 'Bienvenida', title: 'Para qué sirve esta guía' },
  { id: 'circuito', label: 'Recorrido', eyebrow: 'De punta a punta', title: 'Cómo avanza un caso' },
  { id: 'derivacion', label: 'Derivación', eyebrow: 'Gestión Derivación', title: 'Derivar y coordinar' },
  { id: 'emision', label: 'Emisión', eyebrow: 'Recetas a emitir', title: 'Registrar cada receta' },
  { id: 'whatsapp', label: 'WhatsApp', eyebrow: 'Avisos al paciente', title: 'Cuándo se envía cada mensaje' },
  { id: 'catalogo', label: 'Catálogo', eyebrow: 'Visibilidad', title: 'Médicos y administrativos' },
  { id: 'seguimiento', label: 'Seguimiento', eyebrow: 'Consultas', title: 'Historial y farmacia' },
  { id: 'accesos', label: 'Accesos', eyebrow: 'Tu usuario', title: 'Qué pantallas podés usar' },
  { id: 'faq', label: 'Consultas', eyebrow: 'Ayuda rápida', title: 'Preguntas frecuentes' }
];

function NoteList({ items, styles }) {
  return (
    <div style={styles.pageStackCompact}>
      {items.map((item) => (
        <div key={item} style={styles.noteRowSoft}>
          <span style={styles.noteAccentSoft} />
          <span style={{ color: '#1e293b', lineHeight: 1.65, fontSize: 14 }}>{item}</span>
        </div>
      ))}
    </div>
  );
}

function StepCards({ steps, styles, compactCardStyle }) {
  return (
    <div style={styles.pageStack}>
      {steps.map((step) => (
        <article key={step.title} style={compactCardStyle}>
          <h3 style={styles.cardTitle}>{step.title}</h3>
          <p style={{ ...styles.copy, marginBottom: 0 }}>{step.body}</p>
        </article>
      ))}
    </div>
  );
}

function AccesosTable({ styles, compact }) {
  return (
    <div style={{ display: 'grid', gap: compact ? 6 : 8 }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: compact ? '1fr' : 'minmax(160px, 0.4fr) 1fr',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 12,
        background: 'linear-gradient(135deg, #163d6b 0%, #2365ab 100%)',
        color: '#f8fbff',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.06em',
        textTransform: 'uppercase'
      }}>
        {!compact && <span>Pantalla</span>}
        <span>Qué hacés ahí</span>
      </div>
      {accesosOperativos.map((row) => (
        <article
          key={row.pantalla}
          style={{
            ...styles.infoCard,
            padding: compact ? 10 : 12,
            display: 'grid',
            gridTemplateColumns: compact ? '1fr' : 'minmax(160px, 0.4fr) 1fr',
            gap: compact ? 4 : 10,
            alignItems: 'start'
          }}
        >
          <div style={{ fontSize: compact ? 12 : 13, fontWeight: 800, color: '#163d6b', lineHeight: 1.35 }}>
            {row.pantalla}
          </div>
          <div style={{ fontSize: compact ? 12 : 13, color: '#334155', lineHeight: 1.55 }}>
            {row.descripcion}
          </div>
        </article>
      ))}
    </div>
  );
}

function renderManualPage(pageId, styles, { isMobile, forExport, manualPages: pages }) {
  const compactCardStyle = {
    ...styles.infoCard,
    padding: forExport ? 14 : styles.infoCard.padding,
    boxShadow: forExport ? 'none' : styles.infoCard.boxShadow
  };

  switch (pageId) {
    case 'portada':
      if (forExport) {
        return (
          <div style={styles.exportCoverShell}>
            <div style={styles.exportCoverOverline}>Guía de trabajo</div>
            <div style={styles.exportCoverBrand}>Pulse · Administración</div>
            <h1 style={styles.exportCoverTitle}>Manual del área administrativa</h1>
            <p style={styles.exportCoverText}>
              Derivación, emisión de recetas y avisos WhatsApp — junio 2026.
            </p>
            <div style={styles.exportCoverDivider} />
            <div style={styles.exportCoverFooter}>
              <span style={styles.exportCoverFooterItem}>Derivación</span>
              <span style={styles.exportCoverFooterItem}>Emisión</span>
              <span style={styles.exportCoverFooterItem}>WhatsApp</span>
            </div>
          </div>
        );
      }

      return (
        <div style={styles.coverPageShell}>
          <div style={styles.coverPageGlow} />
          <div style={styles.coverPageTopline}>Guía de trabajo</div>
          <div style={styles.coverPageBrand}>Pulse · Área administrativa</div>
          <h1 style={styles.coverPageTitle}>Manual del área administrativa</h1>
          <p style={styles.coverPageText}>
            Cómo derivar pedidos, registrar emisiones y entender los avisos WhatsApp del circuito de recetas.
          </p>
          <div style={styles.coverPageEditionRow}>
            <span style={styles.coverPageEditionChip}>Junio 2026</span>
            <span style={styles.coverPageEditionChip}>{pages.length} capítulos</span>
            <span style={styles.coverPageEditionChip}>Actualizado</span>
          </div>
          <div style={styles.coverPageDivider} />
          <div style={styles.coverPageMetaGrid}>
            <div style={styles.coverPageMetaCard}>
              <div style={styles.coverPageMetaLabel}>Pantalla 1</div>
              <div style={styles.coverPageMetaValue}>Gestión Derivación</div>
            </div>
            <div style={styles.coverPageMetaCard}>
              <div style={styles.coverPageMetaLabel}>Pantalla 2</div>
              <div style={styles.coverPageMetaValue}>Recetas a emitir</div>
            </div>
            <div style={styles.coverPageMetaCard}>
              <div style={styles.coverPageMetaLabel}>Tu rol</div>
              <div style={styles.coverPageMetaValue}>Ordenar el proceso</div>
            </div>
          </div>
        </div>
      );

    case 'alcance':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>¿Para quién es?</h3>
            <p style={styles.copy}>
              Para el área administrativa que organiza recetas: deriva al médico, da seguimiento
              y registra emisiones. No aprobás ni rechazás tratamientos.
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Qué vas a aprender</h3>
            <NoteList
              styles={styles}
              items={[
                'Derivar pedidos y corregir médico o comentario desde el historial.',
                'Coordinarte con el médico por el canal interno (el paciente no lo ve).',
                'Registrar emisiones en Recetas a emitir con número y enlace.',
                'Entender cuándo el paciente recibe WhatsApp de alta o de emisión.'
              ]}
            />
          </article>
          <div style={styles.highlightPanel}>
            <div style={styles.highlightTitle}>Permisos</div>
            <p style={{ ...styles.highlightPanelText, marginBottom: 0 }}>
              Solo ves en el menú las pantallas que te asignó tu responsable. Si falta algo, pedí acceso en Admin Permisos.
            </p>
          </div>
        </div>
      );

    case 'circuito':
      return <StepCards steps={circuitoAdmin} styles={styles} compactCardStyle={compactCardStyle} />;

    case 'derivacion':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Entrada y panorama</h3>
            <p style={styles.copy}>
              Menú Recetas → <strong>Gestión Derivación</strong>. Arriba ves pendientes de derivación y médicos disponibles.
              La pantalla se actualiza sola. Usá el buscador por paciente, DNI o medicamento.
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Derivar — paso a paso</h3>
            <ol style={styles.list}>
              <li>Abrí la tarjeta y verificá paciente y medicamento.</li>
              <li>Elegí médico en el desplegable (filtrá por nombre o matrícula).</li>
              <li>Completá comentario para el médico si hace falta.</li>
              <li>Pulsá <strong>Derivar al médico</strong> y confirmá.</li>
            </ol>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Historial y canal interno</h3>
            <ul style={styles.list}>
              <li><strong>Historial global:</strong> pedidos ya derivados; podés cambiar médico o comentario.</li>
              <li><strong>Médicos con tratamiento:</strong> carga por profesional para repartir trabajo.</li>
              <li><strong>Canal interno</strong> (botón flotante): mensajes con el médico por paciente, sin mezclar al afiliado.</li>
            </ul>
          </article>
        </div>
      );

    case 'emision':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Entrada y lista</h3>
            <p style={styles.copy}>
              Menú Recetas → <strong>Recetas a emitir</strong>. Casos agrupados por paciente y tratamiento.
              KPIs: pendientes de emisión, emitidas, totales. Buscador y paginación arriba.
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>¿Cuándo puedo registrar?</h3>
            <p style={styles.copy}>
              Cuando el médico aprobó y la receta llegó a su ventana de fecha (unos días antes y después de la fecha prevista).
              Las que aún no tocan aparecen como ocultas hasta que el sistema las habilite.
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Registrar emisión — paso a paso</h3>
            <ol style={styles.list}>
              <li>Expandí el grupo del paciente y elegí la receta pendiente habilitada.</li>
              <li>Pulsá <strong>Registrar emisión</strong>.</li>
              <li>Cargá <strong>número de receta</strong> y <strong>enlace</strong> del sistema externo.</li>
              <li>Confirmá con <strong>Registrar</strong>.</li>
            </ol>
          </article>
          <div style={styles.highlightPanelAlt}>
            <div style={styles.highlightTitle}>Emisor administrativo</div>
            <p style={{ ...styles.highlightPanelText, marginBottom: 0 }}>
              Si el médico eligió que emite el equipo administrativo, el caso aparece en tu bandeja de Recetas a emitir.
              Asegurate de estar visible en Administrativos emisión.
            </p>
          </div>
        </div>
      );

    case 'whatsapp':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Quién configura los avisos</h3>
            <p style={styles.copy}>
              El médico activa o desactiva los switches en el paso 2 del wizard de aprobación
              (WhatsApp · Alta tratamiento y WhatsApp · Emitir receta). Vos no los cambiás desde administración.
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Alta tratamiento</h3>
            <p style={styles.copy}>
              Si está <strong>Activo</strong>, se envía al confirmar la aprobación médica (paso 3 del wizard).
              Mensaje con plan aprobado: posología, período y cantidad de recetas.
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Emitir receta</h3>
            <p style={styles.copy}>
              Si está <strong>Activo</strong>, <em>no</em> se envía al aprobar: se envía cuando vos usás
              <strong> Registrar emisión</strong> en Recetas a emitir, con el número y enlace cargados.
            </p>
          </article>
          <div style={styles.highlightPanel}>
            <div style={styles.highlightTitle}>Tip</div>
            <p style={{ ...styles.highlightPanelText, marginBottom: 0 }}>
              Tras registrar, el toast indica si el WhatsApp de emisión se envió o si estaba desactivado en la aprobación.
            </p>
          </div>
        </div>
      );

    case 'catalogo':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Médicos derivación</h3>
            <p style={styles.copy}>
              Recetas → <strong>Médicos derivación</strong>. Interruptor Visible/Oculto por profesional.
              Si al derivar no aparece nadie, un referente debe activar médicos aquí.
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Administrativos emisión</h3>
            <p style={styles.copy}>
              Recetas → <strong>Administrativos emisión</strong>. Define quién del catálogo administrativo
              puede recibir casos cuando el médico asigna emisión al equipo. Misma lógica Visible/Oculto.
            </p>
          </article>
          <div style={styles.highlightPanelAlt}>
            <div style={styles.highlightTitle}>Solo referentes</div>
            <p style={{ ...styles.highlightPanelText, marginBottom: 0 }}>
              Estas pantallas suelen tener acceso limitado. Si derivás o emitís pero no administrás catálogos, pedí ayuda a tu supervisor.
            </p>
          </div>
        </div>
      );

    case 'seguimiento':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Historial global</h3>
            <p style={styles.copy}>
              En Derivación y en Recetas a emitir: botón <strong>Historial global</strong>.
              Filtrá por DNI, fechas, estado o texto libre para casos anteriores.
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Seguimiento en farmacia</h3>
            <p style={styles.copy}>
              Desde recetas emitidas podés abrir el seguimiento del pedido en farmacia
              (preparación, en camino, entregado). Se actualiza mientras tenés el panel abierto.
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Trazabilidad recetas</h3>
            <p style={{ ...styles.copy, marginBottom: 0 }}>
              Si tenés permiso, Recetas → <strong>Trazabilidad recetas</strong> muestra el ciclo completo
              del pedido (creación, derivación, aprobación, emisión). Útil para auditoría y consultas.
            </p>
          </article>
        </div>
      );

    case 'accesos':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Menú según permisos</h3>
            <p style={styles.copy}>
              Cada usuario ve solo lo asignado en Admin Permisos. No ver un ítem no es un error del sistema: pedí el permiso a tu responsable.
            </p>
          </article>
          <AccesosTable styles={styles} compact={isMobile} />
        </div>
      );

    case 'faq':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>No hay médicos al derivar</h3>
            <p style={styles.copy}>
              Activá profesionales en Médicos derivación o pedí a un referente que lo haga.
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>No me deja registrar emisión</h3>
            <p style={styles.copy}>
              Verificá: ¿aprobó el médico? ¿Llegó la fecha prevista? ¿Cargaste número y enlace?
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>El paciente no recibió WhatsApp de emisión</h3>
            <p style={styles.copy}>
              Revisá si el médico tenía activo «Emitir receta» al aprobar. Ese aviso solo sale al registrar emisión, no antes.
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>¿Dónde hablo con el paciente?</h3>
            <p style={{ ...styles.copy, marginBottom: 0 }}>
              En Conversaciones, no en el canal interno médico–administración.
            </p>
          </article>
        </div>
      );

    default:
      return null;
  }
}

export default function AdminManualAdministrativo() {
  return (
    <ManualBookShell
      manualPages={manualPages}
      renderManualPage={renderManualPage}
      pdfFileName="manual-administrativo-pulse-a4.pdf"
      pdfPreviewTitle="Manual administrativo - PDF A4"
    />
  );
}
